import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../../globalFunctions/DateTimeDisplayCleaner";
import AdminUserDrawer from "./AdminUserEdit";
import { useAuth } from "../../../contexts/AuthContext";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function AdminUserTable({ fetchUsers, onEdit, onAdd }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const [availableRoles, setAvailableRoles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/User`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-User-Id": userId,
            },
          }
        );

        if (!response.ok) throw new Error("Erreur serveur");

        const data = await response.json();
        setUsers(data);
      } catch (err) {
        console.error(err);
        setError("Impossible de récupérer les utilisateurs.");
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const loadUsersAndRoles = async () => {
      setLoading(true);
      try {
        // Fetch users
        const userResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/User`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-User-Id": userId,
            },
          }
        );

        if (!userResponse.ok) throw new Error("Erreur chargement utilisateurs");
        const usersData = await userResponse.json();
        setUsers(usersData);

        // Fetch roles (admin-only)
        const rolesResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/User/Roles`,
          {
            headers: {
              "Content-Type": "application/json",
              "X-User-Id": userId,
            },
          }
        );

        if (rolesResponse.ok) {
          const rolesData = await rolesResponse.json();
          setAvailableRoles(rolesData); // [{ id: 1, name: "admin" }, ...]
        } else {
          console.warn("Impossible de charger les rôles. Peut-être non-admin?");
        }
      } catch (err) {
        console.error(err);
        setError("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    };

    loadUsersAndRoles();
  }, [fetchUsers]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsOpenModal(true);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsOpenModal(true);
  };

  const handleSaveUser = (userData) => {
    setUsers((prev) => {
      const exists = prev.some((u) => u.id === userData.id);
      if (exists) {
        // Mise à jour
        return prev.map((u) => (u.id === userData.id ? userData : u));
      } else {
        // Ajout
        return [...prev, userData];
      }
    });

    setIsOpenModal(false);
    setSelectedUser(null);
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.fullName && u.fullName.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="p-6 space-y-6 text-primary">
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">{t("adminUserTable.title")}</h1>
        <div className="flex flex-col md:flex-row gap-2 items-center w-full md:w-auto">
          <input
            type="text"
            placeholder="Rechercher par email ou nom..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input input-bordered w-full md:w-64"
          />
          <button
            onClick={handleAddUser}
            className="btn bg-primary text-white rounded-full"
          >
            {t("adminUserTable.addButton")}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-box border border-base-content/10 bg-base-100">
        <table className="table table-zebra">
          <thead>
            <tr>
              <th>{t("adminUserTable.email")}</th>
              <th>{t("adminUserTable.fullName")}</th>
              <th>{t("adminUserTable.createdAt")}</th>
              <th>{t("adminUserTable.lastSignInAt")}</th>
              <th>{t("adminUserTable.emailConfirmedAt")}</th>
              <th>{t("adminUserTable.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  {t("adminUserTable.loading")}
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="text-center text-red-500">
                  {t("adminUserTable.errorLoading")}
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-gray-500">
                  {t("adminUserTable.noUsersFound")}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user, index) => (
                <tr key={user.id}>
                  <td>{user.email || "-"}</td>
                  <td>{user.fullName || "-"}</td>
                  <td>
                    {user.createdAt ? formatDateTime(user.createdAt) : "-"}
                  </td>
                  <td>
                    {user.lastSignInAt
                      ? formatDateTime(user.lastSignInAt)
                      : "-"}
                  </td>
                  <td>
                    {user.emailConfirmedAt
                      ? formatDateTime(user.emailConfirmedAt)
                      : "-"}
                  </td>
                  <td>
                    <button
                      onClick={() => handleEditUser(user)}
                      className="btn bg-primary text-white rounded-full"
                    >
                      {t("adminUserTable.editButton")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <AdminUserDrawer
        key={selectedUser?.id || "new"}
        isOpen={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
        user={selectedUser}
        availableRoles={availableRoles}
        currentUserId={userId}
      />
    </div>
  );
}
